import { DropDownModel } from "./dropDown";
import { observable, action, computed, makeObservable } from "mobx";
import Validator, { ValidatedValue } from "./validator";
import { ErrorMessages } from "./errorMessage";
import { AddressModel, AddressClass } from "./address";
import { CalculateStore } from "../stores/calculateStore";
import { clientValidators } from "./clientValidators";
import Formatter, { CapitalizationType } from "./formatter";

export interface Phone {
  value: string | undefined;
  errorMessage: string;
}

export interface Email {
  value: string | undefined;
  errorMessage: string;
}

export interface ClientModel {
  id: number;
  legalGroup: ClientLegalGroup;
  legalForm: ValidatedValue<ClientLegalForm | undefined>;
  pesel: ValidatedValue<string>;
  email: Email;
  phone: Phone;
  firstName: ValidatedValue<string>;
  lastName: ValidatedValue<string>;
  companyName: ValidatedValue<string>;
  regon: ValidatedValue<string>;
  nip?: ValidatedValue<string>;
  insurer: boolean;
  insured: boolean;
  personAddress: AddressModel;
  companyAddress: AddressModel;
  contactAddress: AddressModel;
  personAddressTheSame: boolean;
  companyAddressTheSame: boolean;
  contactAddressTheSame: boolean;
  isInsuredInSecondOpinion?: boolean;

  validatePhoneNumber: (required: boolean) => void;
  validateEmail: (required: boolean) => void;
  validateCompanyName: () => void;
  validateInsuredData: () => boolean;
  validateAddress: () => void;

  clearErrorMessages: () => void;

  peselForRequest: string | undefined;
  regonForRequest: string | undefined;
  nipForRequest: string | undefined;
  companyNameForRequest: string | undefined;
  firstNameForRequest: string | undefined;
  lastNameForRequest: string | undefined;
}

export const maxNumberOfInsureds = 3;
export const defaultTuzAccession = 100;
export const defaultOneShareAcquisition = 1000;

export enum ClientLegalGroup {
  PERSON = "PERSON",
  COMPANY = "COMPANY",
  OTHER = "OTHER"
}

export const ClientLegalGroupDropDown: DropDownModel[] = [
  {
    id: ClientLegalGroup.PERSON,
    displayName: "Osoba fizyczna"
  },
  {
    id: ClientLegalGroup.COMPANY,
    displayName: "Firma"
  },
  {
    id: ClientLegalGroup.OTHER,
    displayName: "Inny"
  }
];

export enum ClientLegalForm {
  // company
  JOINT_STOCK_COMPANY = "JOINT_STOCK_COMPANY",
  FOUNDATION = "FOUNDATION",
  ORGANISATIONAL_UNIT_WITHOUT_LEGAL_PERSONALITY = "ORGANISATIONAL_UNIT_WITHOUT_LEGAL_PERSONALITY",
  ADMINISTRATIVE_UNIT = "ADMINISTRATIVE_UNIT",
  STATE_ENTERPRISE = "STATE_ENTERPRISE",
  COOPERATIVE = "COOPERATIVE",
  CIVIL_PARTNERSHIP = "CIVIL_PARTNERSHIP",
  GENERAL_PARTNERSHIP = "GENERAL_PARTNERSHIP",
  LIMITED_PARTNERSHIP = "LIMITED_PARTNERSHIP",
  JOINT_STOCK_LIMITED_PARTNERSHIP = "JOINT_STOCK_LIMITED_PARTNERSHIP",
  LIMITED_LIABILITY_COMPANY = "LIMITED_LIABILITY_COMPANY",
  ASSOCIATION = "ASSOCIATION",
  FOREIGN_SOLE_PROPRIETORSHIP = "FOREIGN_SOLE_PROPRIETORSHIP",

  // private
  PRIVATE = "PRIVATE",

  // other
  OTHER_ENTITY_WITHOUT_LEGAL_FORM = "OTHER_ENTITY_WITHOUT_LEGAL_FORM",
  SOLE_PROPRIETORSHIP = "SOLE_PROPRIETORSHIP"
}

export const ClientCompanyLegalFormDropDown: DropDownModel[] = [
  {
    id: ClientLegalForm.JOINT_STOCK_COMPANY,
    displayName: "Spółka Akcyjna"
  },
  {
    id: ClientLegalForm.FOUNDATION,
    displayName: "Fundacja"
  },
  {
    id: ClientLegalForm.ORGANISATIONAL_UNIT_WITHOUT_LEGAL_PERSONALITY,
    displayName: "Inna jednostka organizacyjna nie posiadająca osobowości prawnej"
  },
  {
    id: ClientLegalForm.ADMINISTRATIVE_UNIT,
    displayName: "Jednostka administracyjna"
  },
  {
    id: ClientLegalForm.STATE_ENTERPRISE,
    displayName: "Przedsiębiorstwo państwowe"
  },
  {
    id: ClientLegalForm.COOPERATIVE,
    displayName: "Spółdzielnia"
  },
  {
    id: ClientLegalForm.CIVIL_PARTNERSHIP,
    displayName: "Spółka cywilna"
  },
  {
    id: ClientLegalForm.GENERAL_PARTNERSHIP,
    displayName: "Spółka jawna"
  },
  {
    id: ClientLegalForm.LIMITED_PARTNERSHIP,
    displayName: "Spółka komandytowa"
  },
  {
    id: ClientLegalForm.JOINT_STOCK_LIMITED_PARTNERSHIP,
    displayName: "Spółka komandytowo-akcyjna"
  },
  {
    id: ClientLegalForm.LIMITED_LIABILITY_COMPANY,
    displayName: "Spółka z ograniczoną odpowiedzialnością"
  },
  {
    id: ClientLegalForm.ASSOCIATION,
    displayName: "Stowarzyszenie"
  },
  {
    id: ClientLegalForm.FOREIGN_SOLE_PROPRIETORSHIP,
    displayName: "Zagraniczna osoba fizyczna"
  }
];

export const ClientPrivateLegalFormDropDown: DropDownModel[] = [
  {
    id: ClientLegalForm.PRIVATE,
    displayName: "Osoba fizyczna nie prowadząca działalności gospodarczej"
  }
];

export const ClientOtherLegalFormDropDown: DropDownModel[] = [
  {
    id: ClientLegalForm.SOLE_PROPRIETORSHIP,
    displayName: "Osoba fizyczna prowadząca działalność gospodarczą"
  },
  {
    id: ClientLegalForm.OTHER_ENTITY_WITHOUT_LEGAL_FORM,
    displayName: "Inna jednostka nie posiadająca osobowości prawnej"
  }
];

export enum ClientDataType {
  PERSON, COMPANY, PERSON_AND_COMPANY
}

export const LegalFormClientData = {
  [ClientLegalForm.PRIVATE]: ClientDataType.PERSON,
  [ClientLegalForm.JOINT_STOCK_COMPANY]: ClientDataType.COMPANY,
  [ClientLegalForm.FOUNDATION]: ClientDataType.COMPANY,
  [ClientLegalForm.ORGANISATIONAL_UNIT_WITHOUT_LEGAL_PERSONALITY]: ClientDataType.COMPANY,
  [ClientLegalForm.ADMINISTRATIVE_UNIT]: ClientDataType.COMPANY,
  [ClientLegalForm.STATE_ENTERPRISE]: ClientDataType.COMPANY,
  [ClientLegalForm.COOPERATIVE]: ClientDataType.COMPANY,
  [ClientLegalForm.GENERAL_PARTNERSHIP]: ClientDataType.COMPANY,
  [ClientLegalForm.LIMITED_PARTNERSHIP]: ClientDataType.COMPANY,
  [ClientLegalForm.JOINT_STOCK_LIMITED_PARTNERSHIP]: ClientDataType.COMPANY,
  [ClientLegalForm.LIMITED_LIABILITY_COMPANY]: ClientDataType.COMPANY,
  [ClientLegalForm.FOREIGN_SOLE_PROPRIETORSHIP]: ClientDataType.COMPANY,
  [ClientLegalForm.ASSOCIATION]: ClientDataType.COMPANY,
  [ClientLegalForm.CIVIL_PARTNERSHIP]: ClientDataType.PERSON_AND_COMPANY,
  [ClientLegalForm.OTHER_ENTITY_WITHOUT_LEGAL_FORM]: ClientDataType.COMPANY,
  [ClientLegalForm.SOLE_PROPRIETORSHIP]: ClientDataType.PERSON_AND_COMPANY
};

export function isClientTuzMember(calculateStore: CalculateStore) {
  const tuzAccession = calculateStore.calculationResponse?.entryFees?.tuzAccession ?? defaultTuzAccession;
  const oneShareAcquisition = calculateStore.calculationResponse?.entryFees?.oneShareAcquisition ?? defaultOneShareAcquisition;

  return tuzAccession === 0 && oneShareAcquisition === 0;
}


interface DefaultClientArguments {
  id?: number;
  legalForm?: ClientLegalForm | undefined;
  legalGroup?: ClientLegalGroup;
  pesel?: string;
  regon?: string;
  nip?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  insurer?: boolean;
  insured?: boolean;
  personAddress?: AddressModel;
  companyAddress?: AddressModel;
  contactAddress?: AddressModel;
  personAddressTheSame?: boolean;
  companyAddressTheSame?: boolean;
  contactAddressTheSame?: boolean;
  isInsuredInSecondOpinion?: boolean;
}

export class DefaultClient implements ClientModel {
  @observable id = 0;
  @observable legalGroup: ClientLegalGroup = ClientLegalGroup.PERSON;
  @observable legalForm: ValidatedValue<ClientLegalForm | undefined> = new ValidatedValue(ClientLegalForm.PRIVATE, clientValidators.legalForm);
  @observable email: { value: string | undefined, errorMessage: string } = { value: undefined, errorMessage: "" };
  @observable phone: { value: string | undefined, errorMessage: string } = { value: undefined, errorMessage: "" };
  @observable pesel: ValidatedValue<string> = new ValidatedValue("", clientValidators.pesel);
  @observable firstName: ValidatedValue<string> = new ValidatedValue("", clientValidators.firstName);
  @observable lastName: ValidatedValue<string> = new ValidatedValue("", clientValidators.lastName);
  @observable regon: ValidatedValue<string> = new ValidatedValue("", clientValidators.regon);
  @observable nip: ValidatedValue<string> = new ValidatedValue("", clientValidators.nip);
  @observable companyName: ValidatedValue<string> = new ValidatedValue("", clientValidators.companyName);
  @observable insurer = true;
  @observable insured = true;
  @observable personAddress: AddressModel = AddressClass.default();
  @observable companyAddress: AddressModel = AddressClass.default();
  @observable contactAddress: AddressModel = AddressClass.default();
  @observable personAddressTheSame = true;
  @observable companyAddressTheSame = true;
  @observable contactAddressTheSame = true;
  @observable isInsuredInSecondOpinion = false;

  constructor(args?: DefaultClientArguments) {
    makeObservable(this);

    this.id = args?.id ?? new Date().getTime();
    this.legalGroup = args?.legalGroup ?? ClientLegalGroup.PERSON;
    this.legalForm.value = this.getLegalForm(args?.legalForm, this.legalGroup);
    this.insurer = args?.insurer ?? true;
    this.insured = args?.insured ?? true;
    this.pesel.value = args?.pesel ?? "";
    this.regon.value = args?.regon ?? "";
    this.nip.value = args?.nip ?? "";
    this.firstName.value = Formatter.capitalizeWords(args?.firstName, CapitalizationType.full) ?? "";
    this.lastName.value = Formatter.capitalizeWords(args?.lastName, CapitalizationType.full) ?? "";
    this.companyName.value = args?.companyName ?? "";
    this.email.value = args?.email ?? "";
    this.phone.value = args?.phone ?? "";
    this.personAddress = args?.personAddress ?? AddressClass.default();
    this.companyAddress = args?.companyAddress ?? AddressClass.default();
    this.contactAddress = args?.contactAddress ?? AddressClass.default();
    this.contactAddressTheSame = args?.contactAddressTheSame ?? true;
    this.personAddressTheSame = args?.personAddressTheSame ?? true;
    this.companyAddressTheSame = args?.companyAddressTheSame ?? true;
    this.isInsuredInSecondOpinion = args?.isInsuredInSecondOpinion ?? false;
  }

  private getLegalForm(legalForm: ClientLegalForm | undefined, legalGroup: ClientLegalGroup) {
    if (!!legalForm) {
      return legalForm;
    }
    return legalGroup === ClientLegalGroup.PERSON ? ClientLegalForm.PRIVATE : undefined;
  }

  @action
  validatePhoneNumber = (required: boolean) => {
    if (this.isRequired(required, this.phone.value)) {
      this.phone.errorMessage = ErrorMessages.required;
    } else if (!Validator.phone(this.phone.value!)) {
      this.phone.errorMessage = ErrorMessages.client.phone;
    } else {
      this.phone.errorMessage = "";
    }
  }

  @action
  public validateEmail = (required: boolean) => {
    if (this.isRequired(required, this.email.value!)) {
      this.email.errorMessage = ErrorMessages.required;
    } else if (!Validator.email(this.email.value!)) {
      this.email.errorMessage = ErrorMessages.client.email;
    } else {
      this.email.errorMessage = "";
    }
  }

  private isRequired(required: boolean, value: any) {
    return required && (!Validator.inNotNullOrUndefined(value) || !Validator.isNotEmpty(value));
  }

  @action
  public validateCompanyName() {
    if (!Validator.inNotNullOrUndefined(this.companyName.value) || !Validator.isNotEmpty(this.companyName.value!)) {
      this.companyName.errorMessage = ErrorMessages.required;
    } else {
      this.companyName.errorMessage = "";
    }
  }

  @action
  public validateInsuredData(): boolean {
    let hasError = false;

    if (this.legalGroup === ClientLegalGroup.PERSON) {
      this.pesel.validate();
      this.firstName.validate();
      this.lastName.validate();
      hasError = !!this.pesel.errorMessage || !!this.firstName.errorMessage || !!this.lastName.errorMessage;

    } else if (this.legalGroup === ClientLegalGroup.COMPANY) {
      this.regon.validate();
      this.validateCompanyName();
      hasError = !!this.regon.errorMessage || !!this.companyName.errorMessage;

    } else if (this.legalGroup === ClientLegalGroup.OTHER) {
      this.pesel.validate();
      this.firstName.validate();
      this.lastName.validate();
      this.regon.validate();
      this.validateCompanyName();
      hasError = !!this.pesel.errorMessage || !!this.firstName.errorMessage || !!this.lastName.errorMessage || !!this.regon.errorMessage || !!this.companyName.errorMessage;
    }

    return hasError;
  }

  @action
  public validateAddress() {
    this.personAddress.validate();
    this.companyAddress.validate();
    this.contactAddress.validate();
  }

  @action
  public clearErrorMessages() {
    this.pesel.clearValidation();
    this.lastName.clearValidation();
    this.firstName.clearValidation();
    this.regon!.errorMessage = "";
    this.companyName!.errorMessage = "";
    this.personAddress.clearValidation();
    this.companyAddress.clearValidation();
    this.contactAddress.clearValidation();
  }

  @computed
  public get peselForRequest() {
    if (!this.legalForm.value) {
      return undefined;
    }
    switch (LegalFormClientData[this.legalForm.value!]) {
      case ClientDataType.PERSON:
      case ClientDataType.PERSON_AND_COMPANY:
        return this.pesel?.value;
    }
    return undefined;
  }

  @computed
  public get regonForRequest() {
    if (!this.legalForm.value) {
      return undefined;
    }
    switch (LegalFormClientData[this.legalForm.value!]) {
      case ClientDataType.COMPANY:
      case ClientDataType.PERSON_AND_COMPANY:
        return this.regon?.value;
    }
    return undefined;
  }

  @computed
  public get nipForRequest() {
    if (!this.legalForm.value) {
      return undefined;
    }
    switch (LegalFormClientData[this.legalForm.value!]) {
      case ClientDataType.COMPANY:
      case ClientDataType.PERSON_AND_COMPANY:
        return this.nip?.value;
    }
    return undefined;
  }

  @computed
  public get companyNameForRequest() {
    if (!this.legalForm.value) {
      return undefined;
    }
    switch (LegalFormClientData[this.legalForm.value!]) {
      case ClientDataType.COMPANY:
      case ClientDataType.PERSON_AND_COMPANY:
        return this.companyName?.value;
    }
    return undefined;
  }

  @computed
  public get firstNameForRequest() {
    if (!this.legalForm.value) {
      return undefined;
    }
    switch (LegalFormClientData[this.legalForm.value!]) {
      case ClientDataType.PERSON:
      case ClientDataType.PERSON_AND_COMPANY:
        return this.firstName?.value;
    }
    return undefined;
  }

  @computed
  public get lastNameForRequest() {
    if (!this.legalForm.value) {
      return undefined;
    }
    switch (LegalFormClientData[this.legalForm.value!]) {
      case ClientDataType.PERSON:
      case ClientDataType.PERSON_AND_COMPANY:
        return this.lastName?.value;
    }
    return undefined;
  }

  static example = (args: DefaultClientArguments) => {
    const client: ClientModel = new DefaultClient(args);
    client.legalGroup = ClientLegalGroup.PERSON;
    client.legalForm.value = ClientLegalForm.PRIVATE;
    if (client.insurer) {
      client.email.value = "test@example.pl";
      client.phone.value = "500500500";
    }
    return client;
  }
}
